import { useEffect, useRef, useState } from "react";
import Axios from "axios";
import { API_URL } from "../../../config";
import {
  INDISPONIBLE,
  REFUS_LIEN,
  codeRefus,
  verifierLien,
} from "../../../helpers/lienAJeton";

export const ETATS = {
  VERIFICATION: "verification",
  INVALIDE: "invalide",
  DEJA_UTILISE: "deja_utilise",
  INDISPONIBLE: "indisponible",
  FORMULAIRE: "formulaire",
  SUCCES: "succes",
};

const ERREUR_GENERIQUE = { other: "Une erreur est survenue, veuillez réessayer." };

const etatDuRefus = (code) => {
  if (code === REFUS_LIEN.DEJA_UTILISE) return ETATS.DEJA_UTILISE;
  if (code === INDISPONIBLE) return ETATS.INDISPONIBLE;
  return ETATS.INVALIDE;
};

// Logique de la page « Créez votre mot de passe » (PER-2398, revue par PER-2509) :
// 1. à l'ouverture, le lien d'invitation est vérifié SANS être consommé : un robot ou
//    l'antivirus d'une messagerie qui ouvre le lien ne grille plus l'invitation ;
// 2. validation locale : mot de passe conforme (PasswordControl → setCanSave) et
//    confirmation identique ;
// 3. à l'enregistrement seulement, échange du lien contre un jeton de définition du
//    mot de passe (`customers/password-definition-token`, qui active le compte), puis
//    enregistrement du mot de passe avec ce second jeton (`customers/reset-password`).
//    Si la seconde étape échoue, le second jeton est gardé : un nouvel essai ne refait
//    que l'enregistrement.
const useActivateAccountInvite = (tokenInvitation) => {
  const [etat, setEtat] = useState(
    tokenInvitation ? ETATS.VERIFICATION : ETATS.INVALIDE
  );
  const [essai, setEssai] = useState(0);
  const [jetonMotDePasse, setJetonMotDePasse] = useState(null);
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [canSave, setCanSave] = useState(false);
  const [erreursApi, setErreursApi] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  // Garde d'unmount pour `soumettre` : évite les mises à jour d'état après
  // démontage du composant (navigation pendant l'appel API).
  const monte = useRef(true);
  useEffect(() => {
    return () => {
      monte.current = false;
    };
  }, []);

  useEffect(() => {
    if (!tokenInvitation) return undefined;
    let actif = true;
    setEtat(ETATS.VERIFICATION);
    verifierLien(tokenInvitation)
      .then(() => {
        if (actif) setEtat(ETATS.FORMULAIRE);
      })
      .catch((err) => {
        if (actif) setEtat(etatDuRefus(codeRefus(err)));
      });
    return () => {
      actif = false;
    };
  }, [tokenInvitation, essai]);

  const passwordsEqual = password.length > 0 && password === passwordConfirm;
  const peutSoumettre = canSave && passwordsEqual && !isSaving;
  const errors = {
    ...erreursApi,
    ...(passwordConfirm.length > 0 && !passwordsEqual
      ? { passwordConfirm: "Doit être identique au premier" }
      : {}),
  };

  const obtenirJetonMotDePasse = () => {
    if (jetonMotDePasse) return Promise.resolve(jetonMotDePasse);
    return Axios.post(API_URL + "customers/password-definition-token", {
      token: tokenInvitation,
    }).then(
      (res) => {
        if (monte.current) setJetonMotDePasse(res.data.token);
        return res.data.token;
      },
      (err) => {
        const code = codeRefus(err);
        // Lien refusé à l'enregistrement (ouvert entre-temps ailleurs, expiré) :
        // la page bascule sur l'explication, le formulaire n'a plus d'objet.
        if (code !== INDISPONIBLE && monte.current) setEtat(etatDuRefus(code));
        throw err;
      }
    );
  };

  const soumettre = () => {
    if (!peutSoumettre) return;
    setIsSaving(true);
    setErreursApi({});
    obtenirJetonMotDePasse()
      .then((jeton) =>
        Axios.post(API_URL + "customers/reset-password", {
          password,
          token: jeton,
        })
      )
      .then(() => {
        if (monte.current) setEtat(ETATS.SUCCES);
      })
      .catch((err) => {
        if (!monte.current) return;
        const donnees = err.response && err.response.data;
        // Erreurs de saisie renvoyées par l'API (4xx) affichées telles quelles ;
        // jamais le message brut d'une erreur serveur.
        setErreursApi(
          donnees && err.response.status < 500 ? donnees : ERREUR_GENERIQUE
        );
      })
      .then(() => {
        if (monte.current) setIsSaving(false);
      });
  };

  const reessayer = () => setEssai((n) => n + 1);

  return {
    etat,
    password,
    setPassword,
    passwordConfirm,
    setPasswordConfirm,
    canSave,
    setCanSave,
    passwordsEqual,
    peutSoumettre,
    errors,
    isSaving,
    soumettre,
    reessayer,
  };
};

export default useActivateAccountInvite;
